/**
 * Who the customer of a case is, and what sales say about it.
 *
 * Shows the role of the sender (customer, agent, Norican colleague, procurement
 * portal), the proposed account with its name and the one-sentence basis, for an agent
 * both the agent and the end customer, and the other candidates with one button each.
 * "Confirm <Name>" accepts the proposal and "Find customer" searches the customers of
 * the case's legal entity: the same words as the customer line, so the sheet never
 * names the action differently (minimal redesign, 12 Oct 2026).
 *
 * Stage 3: an order issued by another Norican company reads "Intercompany order from
 * ..." with a link to the order, the sentences the engine based the account on are
 * listed under the basis, and the other candidates are grouped by invoice account with
 * their city and postcode.
 *
 * A confirmation is stored in the app database only; the engine then evaluates the
 * case again with that customer. No similarity figure is ever shown, and an account
 * the engine only suggests keeps its "Confirm customer" button.
 */

import { Check, Icon, Search } from "@norican/icons";
import {
  Alert,
  Badge,
  Button,
  Card,
  cn,
  Collapsible,
  Dialog,
  Input,
  Skeleton,
  Spinner,
  Typography,
} from "@norican/react";
import { useEffect, useRef, useState } from "react";

import { FeedbackControl } from "@/features/case-review/feedback-control";
import { InstalledBasePanel } from "@/features/case-review/installed-base-panel";
import { customerProposalIdFor, customerProposalOf } from "@/features/case-review/investigation";
import { InvestigationTrail, ProposalTag } from "@/features/case-review/investigation-panel";
import { MatchingSources } from "@/features/case-review/matching-evidence";
import {
  type ApiError,
  type ContactMemoryAccount,
  type CustomerBlock,
  type CustomerCandidate,
  type CustomerHit,
  type CustomerIntercompany,
  memoryAccounts,
  memoryLead,
  multiAccountActive,
  personName,
  SEARCH_MIN_CHARS,
  sourcesSentence,
  type SuggestedCustomer,
  useCaseCustomer,
  useCustomerMemory,
  useCustomerSearch,
  workFailureText,
} from "@/lib/api";
import { logger } from "@/lib/logger";
import { type QuoteCaseDetail } from "@/lib/mock";
import {
  ACTION_LABELS,
  confirmsInOneClick,
  CUSTOMER_CONFIRMED_WORDS,
  CUSTOMER_PARTY_LABELS,
  PICK_CUSTOMER_WORDS,
} from "@/lib/sales-flow";
import { useDialogUsage } from "@/lib/usage-react";
import {
  candidateKindLabel,
  candidateUseLabel,
  customerHowLabel,
  customerRoleLabel,
  evidenceKindLabel,
  formatDay,
  intercompanyProofLabel,
} from "@/lib/work-labels";

/** Candidates in the order sent, grouped by the account their invoices go to. */
export function groupByInvoiceAccount(
  candidates: readonly CustomerCandidate[],
): { invoiceAccount: string | null; candidates: CustomerCandidate[] }[] {
  const groups: { invoiceAccount: string | null; candidates: CustomerCandidate[] }[] = [];
  for (const candidate of candidates) {
    const key = candidate.invoice_account?.trim() || null;
    const group = groups.find((entry) => entry.invoiceAccount === key);
    if (group) {
      group.candidates.push(candidate);
    } else {
      groups.push({ invoiceAccount: key, candidates: [candidate] });
    }
  }
  return groups;
}

/** "8000 Aarhus" from the postcode and the city of a candidate; null without either. */
export function placeOf(
  candidate: Pick<CustomerCandidate, "postcode" | "city"> | null | undefined,
): string | null {
  if (!candidate) {
    return null;
  }
  const place = [candidate.postcode, candidate.city]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" ");
  return place || null;
}

/** The longest text the customer search box takes. */
const SEARCH_MAX_CHARS = 60;

/**
 * The text the customer search starts with (sales review of 12 Oct 2026, C-02): the end
 * customer the mail names, or the company read from the mail, while NO account is
 * proposed ("Find customer"). An agent named an end customer that had no account, and the
 * line offered the agent's own account; now the search opens on the end customer's name.
 * Empty when an account is proposed or a person confirmed one: "Not them" and "Change
 * customer" look for another firm. A note in brackets at the end of the name is dropped
 * ("Example Energy Plc (EXEN)" searches "Example Energy Plc").
 */
export function customerSearchStart(
  detail: Pick<QuoteCaseDetail, "customerBlock" | "work"> | null | undefined,
): string {
  const block = detail?.customerBlock ?? null;
  if (!block || block.account?.trim() || detail?.work?.customer_account?.trim()) {
    return "";
  }
  const name = (block.end_customer?.name ?? block.company ?? "")
    .replace(/\s+/g, " ")
    .replace(/\s*[([][^()[\]]*[)\]]\s*$/, "")
    .trim();
  return firstDistinctiveWord(name).slice(0, SEARCH_MAX_CHARS).trim();
}

/**
 * Words that never tell one firm from another: legal forms, articles and the generic
 * words of a firm name (hunt round 2, Q-20).
 */
const NOT_DISTINCTIVE = new Set([
  "the",
  "and",
  "of",
  "for",
  "ab",
  "ag",
  "as",
  "a/s",
  "aps",
  "bv",
  "co",
  "company",
  "corp",
  "corporation",
  "gmbh",
  "group",
  "inc",
  "kg",
  "limited",
  "llc",
  "ltd",
  "nv",
  "oy",
  "plc",
  "pvt",
  "sa",
  "sas",
  "sl",
  "spa",
  "srl",
  "sro",
  "s.r.o.",
]);

/**
 * The first word of a firm name that tells it from others (hunt round 2, Q-20): a search
 * for two words of an end customer found nothing ("KMC AB", "The Score Group"), one word
 * found the firm. "The Score Group" gives "Score", "KMC AB" gives "KMC". A name of
 * generic words only is kept whole.
 */
export function firstDistinctiveWord(name: string): string {
  const words = name.split(/\s+/).filter(Boolean);
  const word = words.find(
    (entry) =>
      !NOT_DISTINCTIVE.has(entry.toLowerCase().replace(/[.,]+$/, "")) &&
      entry.replace(/[^\p{L}\p{N}]/gu, "").length >= 2,
  );
  // A word keeps its letters and digits: no quote marks or brackets around it.
  return word ? word.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, "") : name;
}

/** One account the case already holds, listed in "Accounts on this case" (Q-20). */
export interface CaseAccount {
  account: string;
  name: string | null;
  city: string | null;
  postcode: string | null;
  /** Why it is on the case: the suggestion, a party of the mail, or a candidate. */
  note: string;
}

/** Most accounts listed under "Accounts on this case". */
const MAX_CASE_ACCOUNTS = 8;

/**
 * The accounts the case already holds (hunt round 2, Q-20), in order: what I2Q suggests,
 * the agent and the end customer of an agent's mail, then the candidates. One row per
 * account. The pick customer dialog lists them above the search hits, so an empty search
 * never hides an account the case knows.
 */
export function caseAccountsOf(
  detail: Pick<QuoteCaseDetail, "customerBlock">,
  suggested: readonly SuggestedCustomer[] = [],
): CaseAccount[] {
  const out: CaseAccount[] = [];
  const add = (entry: CaseAccount) => {
    if (entry.account && !out.some((row) => row.account === entry.account)) {
      out.push(entry);
    }
  };
  for (const customer of suggested) {
    add({
      account: customer.account,
      name: customer.name ?? null,
      city: customer.city ?? null,
      postcode: customer.postcode ?? null,
      note: suggestedNote(customer),
    });
  }
  const block = detail.customerBlock ?? null;
  const party = (
    value: { account: string | null; name: string } | null | undefined,
    note: string,
  ) => {
    const account = value?.account?.trim();
    if (account) {
      add({ account, name: value?.name ?? null, city: null, postcode: null, note });
    }
  };
  party(block?.agent, CUSTOMER_PARTY_LABELS.agent);
  party(block?.end_customer, CUSTOMER_PARTY_LABELS.endCustomer);
  for (const candidate of block?.candidates ?? []) {
    add({
      account: candidate.account,
      name: candidate.name || null,
      city: candidate.city ?? null,
      postcode: candidate.postcode ?? null,
      note: candidate.basis?.trim() || "Candidate of this case",
    });
  }
  return out.slice(0, MAX_CASE_ACCOUNTS);
}

/** The place of an account from the candidates that carry it (the shown account included). */
export function placeOfAccount(
  account: string | null | undefined,
  candidates: readonly CustomerCandidate[] | null | undefined,
): string | null {
  if (!account) {
    return null;
  }
  return placeOf((candidates ?? []).find((candidate) => candidate.account === account));
}

function MemoryRows({ accounts }: { accounts: ContactMemoryAccount[] }) {
  return (
    <ul className="flex flex-col">
      {accounts.map((entry) => {
        const sources = sourcesSentence(entry.sources);
        const documents = entry.documents ?? 0;
        return (
          <li
            key={entry.account}
            className="border-border-subtle flex flex-col border-b py-1.5 last:border-b-0"
          >
            <Account account={entry.account} name={entry.name} />
            <span className="text-fg-muted text-xs break-words">
              {documents} {documents === 1 ? "document" : "documents"}
              {sources ? `: ${sources}` : ""}
              {entry.last_at ? `. Last on ${formatDay(entry.last_at)}` : ""}
            </span>
            {entry.rep_confirmed_by && (
              <span className="text-fg-muted text-xs break-words">
                Confirmed by {personName(entry.rep_confirmed_by)}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** "Why this customer": read from the app database only when opened. */
function WhyThisCustomer({ caseId }: { caseId: string }) {
  const [open, setOpen] = useState(false);
  const memory = useCustomerMemory(caseId, open);
  const view = memory.data ?? null;
  const accounts = view ? memoryAccounts(view.accounts) : [];
  const domain = view ? memoryAccounts(view.domain_accounts) : [];
  return (
    <Collapsible.Root open={open} onOpenChange={setOpen}>
      <Collapsible.Trigger className="text-accent-fg-subtle text-xs font-semibold hover:underline">
        {open ? "Hide why this customer" : "Why this customer"}
      </Collapsible.Trigger>
      <Collapsible.Panel>
        <div className="mt-2 flex flex-col gap-2">
          {memory.isLoading ? (
            <Skeleton variant="rect" height={48} />
          ) : memory.isError ? (
            <Typography variant="caption" tone="muted" role="alert">
              I2Q could not read the earlier documents now.
            </Typography>
          ) : !view ? (
            <Typography variant="caption" tone="muted">
              Earlier documents are not available for this case.
            </Typography>
          ) : (
            <>
              <Typography
                variant="caption"
                tone="muted"
                className="break-words"
                data-memory-multi-account={multiAccountActive(view) ? "" : undefined}
              >
                {memoryLead(view)}
              </Typography>
              {view.forwarded_from && (
                <Typography variant="caption" tone="subtle" className="break-words">
                  Forwarded from {view.forwarded_from}
                </Typography>
              )}
              {accounts.length > 0 && <MemoryRows accounts={accounts} />}
              {domain.length > 0 && (
                <>
                  {accounts.length > 0 && (
                    <Typography variant="caption" tone="subtle">
                      Other senders of the same mail domain
                    </Typography>
                  )}
                  <MemoryRows accounts={domain} />
                </>
              )}
            </>
          )}
        </div>
      </Collapsible.Panel>
    </Collapsible.Root>
  );
}

/** "Intercompany order from {buyer}", with a link to the order when it is an attachment. */
function IntercompanyLine({
  caseId,
  intercompany,
}: {
  caseId: string;
  intercompany: CustomerIntercompany;
}) {
  const document = intercompany.document ?? null;
  const index = document?.index;
  const linkText =
    document?.file ?? (document?.doc_number ? `order ${document.doc_number}` : "the order");
  const proof = intercompanyProofLabel(intercompany.proof);
  return (
    <div className="flex flex-col gap-1">
      {proof && (
        <Badge tone="info" variant="outline" size="sm" className="w-fit">
          {proof}
        </Badge>
      )}
      <Typography variant="body-sm" className="text-fg break-words">
        Intercompany order from <span className="font-semibold">{intercompany.buyer_name}</span>
        {typeof index === "number" ? (
          <>
            {" ("}
            <a
              href={`/api/cases/${encodeURIComponent(caseId)}/attachments/${index}`}
              download={document?.file ?? undefined}
              className="text-accent-fg-subtle break-all hover:underline"
            >
              {linkText}
            </a>
            {")"}
          </>
        ) : document?.doc_number ? (
          ` (order ${document.doc_number})`
        ) : null}
      </Typography>
    </div>
  );
}

function Account({ account, name }: { account: string | null | undefined; name?: string | null }) {
  return (
    <span className="text-fg min-w-0 text-sm break-words">
      {name && <span className="font-semibold">{name}</span>}
      {account && (
        <span className="text-fg-muted font-mono text-xs">
          {name ? " " : ""}
          {account}
        </span>
      )}
    </span>
  );
}

function PartyRow({
  label,
  party,
  note,
}: {
  label: string;
  party: { account: string | null; name: string };
  note?: string;
}) {
  return (
    <div className="flex min-w-0 flex-col">
      <Typography variant="caption" tone="subtle">
        {label}
      </Typography>
      <Account account={party.account} name={party.name} />
      {note && (
        <Typography variant="caption" tone="muted" className="break-words">
          {note}
        </Typography>
      )}
    </div>
  );
}

/**
 * One result row of the customer search. The postcode and the city sit under the name,
 * as on the candidates of the card: accounts of one name (several plants of one group)
 * could not be told apart without them (sales review of 12 Oct 2026, U-03). The "Pick"
 * button names the place too, so two rows of one name are two different buttons. A
 * blocked account is greyed with its mark, has no "Pick", and offers the account its
 * mark points to.
 */
export function CustomerHitRow({
  hit,
  busy,
  onPick,
  marked = false,
  note,
}: {
  hit: CustomerHit;
  busy: boolean;
  onPick: (account: string, name?: string) => void;
  /** The row the arrow keys of the search box point at: Enter picks it. */
  marked?: boolean;
  /** A line under the place: why the row is offered ("Suggested by I2Q"). */
  note?: string;
}) {
  const blocked = hit.blocked === true;
  const redirect = blocked ? (hit.redirect_to ?? null) : null;
  const place = placeOf(hit);
  return (
    <li
      className={cn(
        "border-border-subtle flex items-center gap-3 border-b py-2 last:border-b-0",
        marked && "bg-bg-subtle",
      )}
      data-blocked={blocked ? "true" : undefined}
      data-marked={marked ? "" : undefined}
      aria-current={marked ? "true" : undefined}
    >
      <span className="flex min-w-0 flex-1 flex-col">
        <span className={cn("flex flex-wrap items-center gap-2", blocked && "opacity-60")}>
          <Account account={hit.account} name={hit.name} />
          {blocked && (
            <Badge tone="danger" variant="outline" size="xs">
              Do not use
            </Badge>
          )}
        </span>
        {place && (
          <span
            className={cn("text-fg-muted text-xs break-words", blocked && "opacity-60")}
            data-customer-place={place}
          >
            {place}
          </span>
        )}
        {blocked && hit.note && (
          <span className="text-fg-muted text-xs break-words">{hit.note}</span>
        )}
        {note && (
          <span className="text-fg-muted text-xs break-words" data-customer-suggested>
            {note}
          </span>
        )}
      </span>
      {blocked ? (
        redirect && (
          <Button
            size="sm"
            variant="outline"
            tone="accent"
            disabled={busy}
            aria-label={`Use ${redirect} instead of ${hit.account}`}
            onClick={() => onPick(redirect)}
          >
            Use {redirect}
          </Button>
        )
      ) : (
        <Button
          size="sm"
          variant="outline"
          tone="accent"
          disabled={busy}
          aria-label={place ? `Pick ${hit.name}, ${place}` : `Pick ${hit.name}`}
          onClick={() => onPick(hit.account, hit.name)}
        >
          Pick
        </Button>
      )}
    </li>
  );
}

export interface PickCustomerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  caseId: string;
  busy: boolean;
  error: string | null;
  /** The picked account, with its name when the list knows it (a redirect gives none). */
  onPick: (account: string, name?: string) => void;
  /**
   * The text the search box holds when the dialog opens (`customerSearchStart(detail)`:
   * the end customer the mail names while no account is proposed). Read once, when the
   * dialog mounts; the sales person can change or clear it.
   */
  initialSearch?: string | null;
  /**
   * The accounts I2Q suggests for the case. Before a search they are the first rows of
   * the list, each with the line that says how well it is founded (ease round, 13 Oct
   * 2026: the dialog opened empty next to a customer line that offered the same account).
   */
  suggested?: readonly SuggestedCustomer[];
  /**
   * Hunt round 2 (Q-20): every account the case holds (`caseAccountsOf`), listed as
   * "Accounts on this case" above the search hits, whatever the search says. Without it
   * the suggested accounts stand in.
   */
  caseAccounts?: readonly CaseAccount[];
}

/** The line under a suggested account in the customer search: how well it is founded. */
export function suggestedNote(customer: Pick<SuggestedCustomer, "strength">): string {
  return confirmsInOneClick(customer) ? "Suggested by I2Q" : "Suggested by I2Q: a guess";
}

/** The row the arrow keys point at after a key: one step, kept inside the list. */
export function markedAfterKey(
  marked: number,
  key: "ArrowDown" | "ArrowUp",
  count: number,
): number {
  if (count <= 0) {
    return -1;
  }
  const next = key === "ArrowDown" ? marked + 1 : marked - 1;
  return Math.max(0, Math.min(count - 1, next));
}

/**
 * Search the customers of the case's legal entity and pick one. An account marked do
 * not use in F&O (CC-02) is listed greyed with the mark, has no "Pick" button, and
 * offers the account its mark points to when F&O has one. The server refuses a blocked
 * account anyway (422); that sentence shows under the list like any other refusal.
 * With `initialSearch` the search runs at once on the name the mail gives (C-02).
 *
 * The focus is in the search box when the dialog opens. Arrow down and Arrow up move
 * through the rows, Enter picks the marked row (ease round, 13 Oct 2026: the focus was on
 * "Close dialog" and the keys did nothing).
 */
export function PickCustomerDialog({
  open,
  onOpenChange,
  caseId,
  busy,
  error,
  onPick,
  initialSearch,
  suggested = [],
  caseAccounts,
}: PickCustomerDialogProps) {
  const [text, setText] = useState(() => (initialSearch ?? "").slice(0, SEARCH_MAX_CHARS));
  // The row the arrow keys point at; -1 before the first arrow key.
  const [marked, setMarked] = useState(-1);
  const search = useCustomerSearch(caseId, text);
  // The usage log: opened, saved, or closed without a save. "Saved" is a close after a
  // pick and without an error. Never the search text and never the account.
  const { saved } = useDialogUsage("pick_customer", open, caseId);
  const picked = useRef(false);
  const failed = useRef(false);
  useEffect(() => {
    failed.current = Boolean(error);
  }, [error]);
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    return () => {
      if (picked.current && !failed.current) {
        saved();
      }
      picked.current = false;
    };
  }, [open, saved]);
  const pick = (account: string, name?: string) => {
    picked.current = true;
    onPick(account, name);
  };
  const typed = text.trim();
  const ready = typed.length >= SEARCH_MIN_CHARS;
  const searching = ready && (search.isFetching || search.term !== typed);
  const searchStatus = (search.error as ApiError | null)?.status;
  const hits = search.data ?? [];
  // The accounts the case holds stay above the search hits, whatever the search says
  // (hunt round 2, Q-20: the dialog opened on a search that found nothing and hid them).
  const onCase = caseAccounts ?? caseAccountsOf({}, suggested);
  const offered: CustomerHit[] = onCase.map((entry) => ({
    account: entry.account,
    name: entry.name ?? entry.account,
    entity: "",
    city: entry.city,
    postcode: entry.postcode,
  }));
  const rows = [...offered, ...(!searching && ready && !search.isError ? hits : [])];
  const pickRow = (hit: CustomerHit | undefined) => {
    if (!hit || busy) {
      return;
    }
    if (hit.blocked) {
      if (hit.redirect_to) {
        pick(hit.redirect_to);
      }
      return;
    }
    pick(hit.account, hit.name);
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Popup>
          <Dialog.Header>
            <Dialog.Title>
              <Typography as="span" variant="heading-6" className="text-fg">
                Pick the customer
              </Typography>
            </Dialog.Title>
            <Dialog.CloseButton />
          </Dialog.Header>
          <Dialog.Body className="flex flex-col gap-3">
            <Dialog.Description>
              <Typography as="span" variant="body-sm" tone="muted">
                Search by name or account number. Only customers of this case&apos;s company are
                listed.
              </Typography>
            </Dialog.Description>
            <Input
              type="search"
              value={text}
              maxLength={SEARCH_MAX_CHARS}
              onChange={(event) => {
                setText(event.target.value);
                setMarked(-1);
              }}
              onKeyDown={(event) => {
                if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                  event.preventDefault();
                  setMarked(markedAfterKey(marked, event.key, rows.length));
                } else if (event.key === "Enter" && marked >= 0) {
                  event.preventDefault();
                  pickRow(rows[marked]);
                }
              }}
              placeholder="Customer name or account number"
              aria-label="Search customers"
              autoFocus
              startSlot={<Icon icon={Search} size="sm" className="text-fg-subtle" />}
            />
            {offered.length > 0 && (
              <div className="flex flex-col gap-1">
                <Typography variant="caption" tone="subtle" data-case-accounts-title>
                  {PICK_CUSTOMER_WORDS.onCase}
                </Typography>
                <ul className="flex max-h-80 flex-col overflow-y-auto" data-suggested-customers>
                  {offered.map((hit, index) => (
                    <CustomerHitRow
                      key={`suggested-${hit.account}`}
                      hit={hit}
                      busy={busy}
                      onPick={pick}
                      marked={index === marked}
                      note={onCase[index]?.note}
                    />
                  ))}
                </ul>
              </div>
            )}
            {typed.length > 0 && !ready && (
              <Typography variant="caption" tone="subtle">
                Type at least {SEARCH_MIN_CHARS} characters.
              </Typography>
            )}
            {searching && <Spinner size="sm" label="Searching" />}
            {!searching && ready && search.isError && (
              <Typography variant="caption" className="text-danger-fg" role="alert">
                {searchStatus === 422
                  ? (workFailureText(search.error, "customer") ?? "")
                  : "The customer list is not reachable right now."}
              </Typography>
            )}
            {!searching &&
              ready &&
              !search.isError &&
              (hits.length === 0 ? (
                <Typography variant="caption" tone="subtle" data-no-customer-match>
                  {PICK_CUSTOMER_WORDS.noMatch}
                </Typography>
              ) : (
                <ul className="flex max-h-80 flex-col overflow-y-auto">
                  {hits.map((hit, index) => (
                    <CustomerHitRow
                      key={`${hit.entity}-${hit.account}`}
                      hit={hit}
                      busy={busy}
                      onPick={pick}
                      marked={offered.length + index === marked}
                    />
                  ))}
                </ul>
              ))}
            {error && (
              <Alert tone="warning" title="Not saved">
                {error}
              </Alert>
            )}
          </Dialog.Body>
          <Dialog.Footer className="flex justify-end gap-2">
            <Button variant="outline" tone="neutral" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          </Dialog.Footer>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Alternatives shown before "Show N more". */
const SHOWN_ALTERNATIVES = 3;

/** The customer card of a case. Renders nothing for a case without a customer block. */
export function CustomerCard({ detail }: { detail: QuoteCaseDetail }) {
  const block: CustomerBlock | null | undefined = detail.customerBlock;
  const confirm = useCaseCustomer(detail.id);
  const [picking, setPicking] = useState(false);
  const [showAll, setShowAll] = useState(false);
  if (!block) {
    return null;
  }
  // Mail that is not an inquiry has no customer to settle.
  const idle =
    detail.state === "not-an-rfq" || detail.state === "parked" || detail.state === "duplicate";
  if (idle && !block.account && (block.candidates ?? []).length === 0) {
    return null;
  }

  const work = detail.work ?? null;
  const confirmed = block.confirmed === true || Boolean(work?.customer_account);
  const confirmedAccount = work?.customer_account ?? block.account ?? null;
  const evaluating = detail.state === "queued" || detail.state === "processing";
  const role = customerRoleLabel(block.role);
  const error = workFailureText(confirm.error, "customer");
  // The account already shown on top is not offered a second time.
  const shownAccount = confirmed ? confirmedAccount : (block.account ?? null);
  const candidates = (block.candidates ?? []).filter(
    (candidate) => candidate.account !== shownAccount,
  );
  // The first few alternatives; the rest one click away ("Show N more").
  const hidden = showAll ? 0 : Math.max(0, candidates.length - SHOWN_ALTERNATIVES);
  const candidateGroups = groupByInvoiceAccount(
    hidden > 0 ? candidates.slice(0, SHOWN_ALTERNATIVES) : candidates,
  );
  const grouped = candidateGroups.some((group) => group.invoiceAccount !== null);
  const evidence = block.evidence ?? [];
  const shownPlace = placeOfAccount(shownAccount, block.candidates);
  const howLabel = confirmed ? null : customerHowLabel(block.how);
  // Earlier documents only exist for a case with a sender; a case not for us has none.
  const showMemory = !idle;

  // The proposal of the deeper search, when it is not the account the case is settled on.
  const proposal = customerProposalOf(detail);

  const send = (account: string | null, after?: () => void) => {
    // A confirm of the proposed account names the proposal, as on the customer line.
    const proposalId = customerProposalIdFor(detail.investigation, account);
    logger.debug("[case] customer", { case: detail.id, account, proposal: proposalId });
    confirm.mutate(proposalId ? { account, proposal_id: proposalId } : { account }, {
      onSuccess: after,
    });
  };

  return (
    <Card variant="outline" className="overflow-hidden">
      <Card.Header padding="sm" className="border-border-subtle flex items-center gap-2 border-b">
        <Typography variant="label">Customer</Typography>
        {role && (
          <Badge tone="neutral" variant="outline" size="sm">
            Sender: {role}
          </Badge>
        )}
        {confirmed ? (
          // a person decided: the same word as on the customer line (C-06)
          <Badge tone="success" size="sm" className="ml-auto">
            {CUSTOMER_CONFIRMED_WORDS.bySales}
          </Badge>
        ) : block.confident && block.account ? (
          // I2Q is certain from its own evidence
          <Badge tone="success" variant="subtle" size="sm" className="ml-auto">
            {CUSTOMER_CONFIRMED_WORDS.byI2q}
          </Badge>
        ) : block.suggested ? (
          <Badge tone="warning" size="sm" className="ml-auto">
            Suggested, please confirm
          </Badge>
        ) : null}
      </Card.Header>
      <Card.Body className="flex flex-col gap-3">
        {confirmed ? (
          <>
            <Account
              account={confirmedAccount}
              name={confirmedAccount === block.account ? block.name : null}
            />
            {shownPlace && <span className="text-fg-muted text-xs break-words">{shownPlace}</span>}
            <Typography variant="body-sm" tone="muted">
              Customer confirmed
              {work?.customer_by ? ` by ${personName(work.customer_by)}` : ""}.
              {evaluating ? " I2Q evaluates the case again." : ""}
            </Typography>
          </>
        ) : block.account ? (
          <>
            <Account account={block.account} name={block.name ?? block.company} />
            {shownPlace && <span className="text-fg-muted text-xs break-words">{shownPlace}</span>}
            {howLabel && (
              <Badge tone="neutral" variant="outline" size="sm" className="w-fit">
                {howLabel}
              </Badge>
            )}
            {block.basis && (
              <Typography variant="body-sm" tone="muted" className="break-words">
                {block.basis}
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body-sm" tone="muted" className="break-words">
            {block.basis ?? "I2Q did not identify the customer. Pick the customer of this mail."}
          </Typography>
        )}

        {block.intercompany && (
          <IntercompanyLine caseId={detail.id} intercompany={block.intercompany} />
        )}
        {!confirmed && evidence.length > 0 && (
          <ul className="flex flex-col gap-0.5">
            {evidence.map((entry, index) => {
              const kind = evidenceKindLabel(entry.kind);
              return (
                <li key={index} className="text-fg-muted text-xs break-words">
                  {kind && <span className="text-fg-subtle font-semibold">{kind}: </span>}
                  {entry.text}
                </li>
              );
            })}
          </ul>
        )}
        {showMemory && <WhyThisCustomer caseId={detail.id} />}
        {showMemory && <InstalledBasePanel caseId={detail.id} />}

        {block.agent && <PartyRow label="Agent" party={block.agent} />}
        {block.end_customer && (
          <PartyRow
            label="End customer"
            party={block.end_customer}
            note={block.end_customer.evidence}
          />
        )}
        {block.forwarded_from && (
          <Typography variant="caption" tone="muted" className="break-words">
            Forwarded by {block.forwarded_from}
          </Typography>
        )}
        {block.portal && (
          <Typography variant="caption" tone="muted" className="break-words">
            Sent through {block.portal}
          </Typography>
        )}
        {block.question && !confirmed && (
          <Typography variant="body-sm" className="break-words">
            {block.question}
          </Typography>
        )}
        <MatchingSources sources={block.source_evidence} />
        {/* The proposal of the deeper search with its lookups, as "Why" on the customer
            line shows it (round 2). The buttons are on the customer line. */}
        {proposal && (
          <div className="flex flex-col gap-1.5" data-card-proposal={proposal.id}>
            <span className="flex flex-wrap items-center gap-1.5 text-sm">
              <ProposalTag proposal={proposal} />
              <span className="text-fg break-words">{proposal.name}</span>
              <span className="text-fg-muted font-mono">{proposal.account}</span>
            </span>
            <InvestigationTrail proposal={proposal} />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {!confirmed && block.account && !block.confident && (
            <Button
              size="sm"
              tone="accent"
              loading={confirm.isPending}
              startIcon={<Icon icon={Check} size="xs" />}
              onClick={() => send(block.account ?? null)}
            >
              {ACTION_LABELS.confirmCustomer(
                block.name ?? block.company ?? `Account ${block.account}`,
              )}
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            tone="neutral"
            disabled={confirm.isPending}
            onClick={() => {
              confirm.reset();
              setPicking(true);
            }}
          >
            {ACTION_LABELS.findCustomer}
          </Button>
          {confirmed && work?.customer_account && (
            <Button
              size="sm"
              variant="ghost"
              tone="neutral"
              disabled={confirm.isPending}
              onClick={() => send(null)}
            >
              Undo
            </Button>
          )}
        </div>

        {candidates.length > 0 && (
          <div className="flex flex-col gap-1">
            <Typography variant="caption" tone="subtle">
              Other possible customers
            </Typography>
            {candidateGroups.map((group) => (
              <div key={group.invoiceAccount ?? "none"} className="flex flex-col">
                {grouped && (
                  <span className="text-fg-subtle mt-1 text-xs font-semibold">
                    {group.invoiceAccount
                      ? `Invoice account ${group.invoiceAccount}`
                      : "No shared invoice account"}
                  </span>
                )}
                <ul className="flex flex-col">
                  {group.candidates.map((candidate) => {
                    const kind = candidateKindLabel(candidate.kind);
                    const place = placeOf(candidate);
                    return (
                      <li
                        key={candidate.account}
                        className="border-border-subtle flex items-center gap-3 border-b py-2 last:border-b-0"
                      >
                        <span className="flex min-w-0 flex-1 flex-col">
                          <Account account={candidate.account} name={candidate.name} />
                          {place && (
                            <span className="text-fg-muted text-xs break-words">{place}</span>
                          )}
                          {kind && (
                            <span className="text-fg-muted text-xs break-words">{kind}</span>
                          )}
                          {candidate.basis && (
                            <span className="text-fg-muted text-xs break-words">
                              {candidate.basis}
                            </span>
                          )}
                          <MatchingSources sources={candidate.source_evidence} />
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          tone="neutral"
                          disabled={confirm.isPending}
                          aria-label={candidateUseLabel(candidate)}
                          onClick={() => send(candidate.account)}
                        >
                          Use this customer
                        </Button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
            {hidden > 0 && (
              <Button
                size="sm"
                variant="ghost"
                tone="accent"
                className="w-fit"
                onClick={() => setShowAll(true)}
              >
                Show {hidden} more
              </Button>
            )}
          </div>
        )}

        {error && !picking && (
          <Typography variant="caption" className="text-danger-fg" role="alert">
            {error}
          </Typography>
        )}

        {showMemory && (
          <FeedbackControl
            caseId={detail.id}
            lineIndex={null}
            className="border-border-subtle flex flex-col gap-1 border-t pt-2"
          />
        )}
      </Card.Body>

      {picking && (
        <PickCustomerDialog
          open
          onOpenChange={setPicking}
          caseId={detail.id}
          busy={confirm.isPending}
          error={error}
          onPick={(account) => send(account, () => setPicking(false))}
          initialSearch={customerSearchStart(detail)}
          caseAccounts={caseAccountsOf(detail)}
        />
      )}
    </Card>
  );
}
